import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'swamed_notifications';
const NotificationContext = createContext(null);

function readFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeToStorage(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState(() => readFromStorage());
  // Dedupe: track ids we've already added this session
  const seenIds = useRef(new Set(readFromStorage().map(n => n.id)));

  // Sync from other tabs/windows via storage event
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      const updated = readFromStorage();
      setNotifications(updated);
      updated.forEach(n => seenIds.current.add(n.id));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addNotification = useCallback((notif) => {
    setNotifications(prev => {
      // Dedupe by type+text within last 10s to avoid storm
      const now = Date.now();
      const isDupe = prev.some(
        n => n.type === notif.type && n.text === notif.text &&
          now - new Date(n.time).getTime() < 10000
      );
      if (isDupe) return prev;

      const id = now + Math.random();
      seenIds.current.add(id);
      const next = [{ id, time: new Date(), ...notif }, ...prev].slice(0, 50);
      writeToStorage(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    writeToStorage([]);
  }, []);

  const dismiss = useCallback((id) => {
    setNotifications(prev => {
      const next = prev.filter(n => n.id !== id);
      writeToStorage(next);
      return next;
    });
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, addNotification, clearAll, dismiss }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
